import {
    getFormattedValueAndUnitFromMonetaryValue,
    getFormattedValueAndUnitFromPercentage,
    getValueCheckResultTextColor,
    MetricGrid,
    tokens,
} from 'ComponentLibrary'
import type { MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'

type ValueCheckOverviewMetricGridProps = {
    valueCheck: {
        confirmed?: boolean
        delta_market_value?: number | null
        market_value?: MonetaryValueDataObjectType
    }
    baseValuationMortgageLendingValue?: MonetaryValueDataObjectType | null
    copy?: ValueCheckOverviewMetricGridCopy
    locale?: string
}

export type ValueCheckOverviewMetricGridCopy = {
    subheadlines: {
        changeFromPreviousValuation: string
        marketValue: string
    }
    statusLabels: {
        marketValueConfirmed: string
        marketValueNotConfirmed: string
    }
    comparisonLabels: {
        greaterThanMarketValue: string
        equalToMarketValue: string
        lessThanMarketValue: string
    }
}

const DEFAULT_VALUE_CHECK_OVERVIEW_METRIC_GRID_COPY: ValueCheckOverviewMetricGridCopy = {
    subheadlines: {
        changeFromPreviousValuation: 'Veränderung zum Vorgutachten',
        marketValue: 'Marktwert',
    },
    statusLabels: {
        marketValueConfirmed: 'Marktwert bestätigt',
        marketValueNotConfirmed: 'Marktwert nicht bestätigt',
    },
    comparisonLabels: {
        greaterThanMarketValue: 'BelWert > Marktwert',
        equalToMarketValue: 'BelWert = Marktwert',
        lessThanMarketValue: 'BelWert < Marktwert',
    },
}

const getMortgageLendingToMarketValueComparison = ({
    baseValuationMortgageLendingValue,
    marketValue,
    copy,
}: {
    baseValuationMortgageLendingValue?: MonetaryValueDataObjectType | null
    marketValue?: MonetaryValueDataObjectType
    copy: ValueCheckOverviewMetricGridCopy
}) => {
    const baseMortgageLendingAmount = baseValuationMortgageLendingValue?.amount
    const marketValueAmount = marketValue?.amount

    if (baseMortgageLendingAmount === undefined || marketValueAmount === undefined) {
        return {
            label: '-',
            color: undefined,
        }
    }

    if (baseMortgageLendingAmount > marketValueAmount) {
        return {
            label: copy.comparisonLabels.greaterThanMarketValue,
            color: tokens.ColorTextDangerSubtle,
        }
    }

    if (baseMortgageLendingAmount === marketValueAmount) {
        return {
            label: copy.comparisonLabels.equalToMarketValue,
            color: tokens.ColorTextBoldest,
        }
    }

    return {
        label: copy.comparisonLabels.lessThanMarketValue,
        color: tokens.ColorTextBoldest,
    }
}

export default function ValueCheckOverviewMetricGrid({
    valueCheck,
    baseValuationMortgageLendingValue,
    copy = DEFAULT_VALUE_CHECK_OVERVIEW_METRIC_GRID_COPY,
    locale = 'de-DE',
}: ValueCheckOverviewMetricGridProps) {
    const resultColor = getValueCheckResultTextColor(valueCheck)
    const mortgageLendingComparison = getMortgageLendingToMarketValueComparison({
        baseValuationMortgageLendingValue,
        marketValue: valueCheck.market_value,
        copy,
    })

    const values = [
        {
            ...getFormattedValueAndUnitFromPercentage(valueCheck.delta_market_value ?? undefined, 2, locale),
            subheadline: copy.subheadlines.changeFromPreviousValuation,
            color: resultColor,
        },
        {
            value: valueCheck.confirmed ? copy.statusLabels.marketValueConfirmed : copy.statusLabels.marketValueNotConfirmed,
            color: valueCheck.confirmed ? tokens.ColorTextPositiveSubtle : tokens.ColorTextDangerSubtle,
        },
        {
            ...getFormattedValueAndUnitFromMonetaryValue(valueCheck.market_value, locale),
            subheadline: copy.subheadlines.marketValue,
        },
        {
            value: mortgageLendingComparison.label,
            color: mortgageLendingComparison.color,
        },
    ]

    return <MetricGrid numColumns={2} values={values} size="small" height="108px" />
}
